// R1b-δ — Immutable Audit Ledger viewer.
// Server-paginated over /api/audit. Row expansion shows full payload,
// prev_hash → entry_hash chain. Legacy + authorized-break chips.
import React, { useEffect, useState } from "react";
import { api } from "../../../lib/api";
import { Input } from "../../../components/ui/input";
import { Button } from "../../../components/ui/button";
import StatusChip from "../../../components/StatusChip";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { ChevronDown, ChevronRight } from "lucide-react";

const ACTIONS = ["", "login", "logout", "authz.simulate", "user.create", "suspend", "authority_rule.create", "tour.completed", "tour.dismissed", "chain_break_authorized", "export.compliance_report"];

export default function AuditLedgerTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [breakIndices, setBreakIndices] = useState(new Set());
  const [action, setAction] = useState("");
  const [actor, setActor] = useState("");
  const [skip, setSkip] = useState(0);
  const [expanded, setExpanded] = useState({});
  const limit = 25;

  const load = async () => {
    const params = new URLSearchParams({ limit: String(limit), skip: String(skip) });
    if (action) params.set("action", action);
    if (actor) params.set("actor_user_id", actor);
    const r = await api.get(`/audit?${params}`);
    setItems(r.data.items || []);
    setTotal(r.data.total || 0);
    try {
      const b = await api.get("/audit/break-authorizations");
      setBreakIndices(new Set((b.data.items || b.data || []).map((x) => x.hash_chain_index)));
    } catch (_) { /* admins may lack this endpoint */ }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [skip, action]);

  return (
    <div data-testid="audit-ledger-body">
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-erf-dark/60 mb-1">Action</div>
          <Select value={action} onValueChange={(v) => { setAction(v); setSkip(0); }}>
            <SelectTrigger className="w-56 h-9" data-testid="audit-filter-action"><SelectValue placeholder="All actions" /></SelectTrigger>
            <SelectContent>
              {ACTIONS.map((a) => <SelectItem key={a || "_all"} value={a || "__all"}>{a || "All actions"}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-erf-dark/60 mb-1">Actor user_id</div>
          <Input value={actor} onChange={(e) => setActor(e.target.value)} className="w-64 h-9 font-mono" data-testid="audit-filter-actor" placeholder="uuid" />
        </div>
        <Button onClick={() => { setSkip(0); load(); }} className="bg-erf-dark text-erf-cream h-9" data-testid="audit-filter-apply">Apply</Button>
        <div className="ml-auto text-xs font-nav text-erf-dark/60" data-testid="audit-total">
          {total.toLocaleString()} entries · {breakIndices.size} authorized break(s)
        </div>
      </div>

      <div className="bg-white border border-erf-borderCream rounded-md overflow-x-auto">
        <table className="w-full erf-table">
          <thead className="bg-erf-creamSecondary">
            <tr>
              <th className="w-8"></th>
              <th className="text-left px-3 py-2">Index</th>
              <th className="text-left px-3 py-2">Timestamp</th>
              <th className="text-left px-3 py-2">Action</th>
              <th className="text-left px-3 py-2">Actor</th>
              <th className="text-left px-3 py-2">Resource</th>
              <th className="text-left px-3 py-2">Entry hash</th>
            </tr>
          </thead>
          <tbody>
            {items.map((row) => {
              const isOpen = !!expanded[row.hash_chain_index];
              const isBreak = breakIndices.has(row.hash_chain_index);
              return (
                <React.Fragment key={row.hash_chain_index}>
                  <tr
                    className="border-t border-erf-borderCream/60 cursor-pointer hover:bg-erf-creamSecondary/40"
                    onClick={() => setExpanded((e) => ({ ...e, [row.hash_chain_index]: !e[row.hash_chain_index] }))}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setExpanded((s) => ({ ...s, [row.hash_chain_index]: !s[row.hash_chain_index] }));
                      }
                    }}
                    tabIndex={0}
                    role="button"
                    aria-expanded={isOpen}
                    aria-label={`Audit entry ${row.hash_chain_index}, action ${row.action}. Press Enter to ${isOpen ? "collapse" : "expand"} details.`}
                    data-testid={`audit-row-${row.hash_chain_index}`}
                  >
                    <td className="px-2" aria-hidden="true">
                      {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </td>
                    <td className="px-3 py-2 mono">{row.hash_chain_index}</td>
                    <td className="px-3 py-2 mono text-[11px]">{row.ts ? new Date(row.ts).toLocaleString() : "—"}</td>
                    <td className="px-3 py-2 font-nav">
                      {row.action}
                      {row.legacy && <span className="ml-2 text-[9px] font-mono uppercase px-1 py-0.5 rounded-sm border border-erf-borderCream text-erf-dark/60">legacy</span>}
                      {isBreak && <span className="ml-2 text-[9px] font-mono uppercase px-1 py-0.5 rounded-sm border border-amber-400 bg-amber-50 text-amber-900" data-testid={`audit-authorized-break-${row.hash_chain_index}`}>authorized break</span>}
                    </td>
                    <td className="px-3 py-2 mono text-[11px]">{(row.actor_user_id || "").slice(0, 8) || "—"}</td>
                    <td className="px-3 py-2 mono text-[11px]">{row.resource_type}{row.resource_id ? `:${String(row.resource_id).slice(0, 8)}` : ""}</td>
                    <td className="px-3 py-2 mono text-[10px] text-erf-dark/60">{row.entry_hash ? row.entry_hash.slice(0, 12) + "…" : "—"}</td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-erf-creamSecondary/40 border-t border-erf-borderCream/60">
                      <td colSpan={7} className="px-4 py-3">
                        <div className="grid gap-2 md:grid-cols-2 text-[11px] font-mono">
                          <div><span className="text-erf-dark/60">prev_hash:</span> {row.prev_hash || "—"}</div>
                          <div><span className="text-erf-dark/60">entry_hash:</span> {row.entry_hash || "—"}</div>
                          <div><span className="text-erf-dark/60">actor_role:</span> {row.actor_role || "—"}</div>
                          <div><span className="text-erf-dark/60">ip / ua:</span> {row.ip || "—"} · {row.ua ? row.ua.slice(0, 40) : "—"}</div>
                        </div>
                        {row.before && (
                          <details className="mt-2">
                            <summary className="text-[10px] font-mono uppercase text-erf-dark/60 cursor-pointer">before</summary>
                            <pre className="mt-1 text-[10px] font-mono bg-white p-2 rounded-sm border border-erf-borderCream overflow-x-auto">{JSON.stringify(row.before, null, 2)}</pre>
                          </details>
                        )}
                        {row.after && (
                          <details className="mt-2">
                            <summary className="text-[10px] font-mono uppercase text-erf-dark/60 cursor-pointer">after</summary>
                            <pre className="mt-1 text-[10px] font-mono bg-white p-2 rounded-sm border border-erf-borderCream overflow-x-auto">{JSON.stringify(row.after, null, 2)}</pre>
                          </details>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-3">
        <Button variant="outline" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - limit))} data-testid="audit-prev">Previous</Button>
        <div className="text-xs font-mono text-erf-dark/60">Rows {skip + 1}–{Math.min(skip + limit, total)} of {total}</div>
        <Button variant="outline" disabled={skip + limit >= total} onClick={() => setSkip(skip + limit)} data-testid="audit-next">Next</Button>
      </div>
    </div>
  );
}
